import type { Tela, TelaDeOrigem } from "../Aplicacao"
import { processo } from "../formato"
import { TELAS } from "../registro-de-telas"
import { telaParaCaminho } from "../roteador"
import { DADOS_DO_ITEM, grupoDoItem, primeiroItem, type GrupoDeConfiguracao } from "../itens-de-configuracao"

/**
 * OS NÍVEIS DA TRILHA DE CADA TELA (spec 0076). Função pura: quem chama passa
 * como navegar, e a `Casca` desenha os níveis no `Breadcrumb`.
 *
 * O nível com `endereco` é um link de verdade (abre em nova aba com Ctrl+clique)
 * e, no clique simples, chama `aoAbrir`, que troca de tela sem recarregar. O
 * último nível é a página atual. O Painel não tem trilha (pedido de DEV,
 * 06/10/2026).
 */
export type NivelDaTrilha = {
  rotulo: string
  endereco?: string
  aoAbrir?: () => void
}

function ligacao(rotulo: string, destino: Tela, navegar: (tela: Tela) => void): NivelDaTrilha {
  return { rotulo, endereco: telaParaCaminho(destino), aoAbrir: () => navegar(destino) }
}

export function niveisDaTrilha(
  tela: Tela,
  processoDoContrato: string | null,
  navegar: (tela: Tela) => void,
  grupos: GrupoDeConfiguracao[] | null = null,
  siglaDaEntidade: string | null = null,
): NivelDaTrilha[] {
  if (tela.nome === "painel") return []
  // CONFIGURAÇÕES, O GRUPO E O ITEM (spec 0104), quando o alcance já chegou.
  if (tela.nome === "configuracoes" && "item" in tela && tela.item !== undefined && grupos !== null) {
    const grupo = grupoDoItem(grupos, tela.item)
    const inicio = primeiroItem(grupos)
    const doGrupo = grupo?.itens[0]
    if (grupo !== undefined && inicio !== undefined && doGrupo !== undefined) {
      if (tela.item === "entidades" && "entidade" in tela && tela.entidade !== undefined) {
        return [
          ligacao("Configurações", { nome: "configuracoes", item: inicio }, navegar),
          ligacao(grupo.rotulo, { nome: "configuracoes", item: doGrupo }, navegar),
          ligacao(DADOS_DO_ITEM.entidades.rotulo, { nome: "configuracoes", item: "entidades" }, navegar),
          { rotulo: siglaDaEntidade ?? "Entidade" },
        ]
      }
      return [
        ligacao("Configurações", { nome: "configuracoes", item: inicio }, navegar),
        ligacao(grupo.rotulo, { nome: "configuracoes", item: doGrupo }, navegar),
        { rotulo: DADOS_DO_ITEM[tela.item].rotulo },
      ]
    }
  }
  // O PRIMEIRO NÍVEL É DE ONDE A PESSOA VEIO (spec 0084), com o nome do menu;
  // sem origem conhecida, a lista de Contratos, como antes.
  const deOnde = (origem: TelaDeOrigem | undefined): NivelDaTrilha =>
    origem === undefined
      ? ligacao("Contratos", { nome: "contratos" }, navegar)
      : ligacao(TELAS[origem.nome].rotulo, origem, navegar)
  if (tela.nome === "ficha") {
    return [deOnde(tela.origem), { rotulo: "Ficha do contrato" }]
  }
  if (tela.nome === "esteira") {
    const ficha: Tela = {
      nome: "ficha",
      contratoId: tela.contratoId,
      aba: tela.abaDaFicha,
      submenu: tela.submenuDaFicha,
      ...(tela.origem === undefined ? {} : { origem: tela.origem }),
    }
    return [
      deOnde(tela.origem),
      ligacao("Ficha do contrato", ficha, navegar),
      { rotulo: `Processo ${processo(processoDoContrato)}` },
    ]
  }
  const registro = TELAS[tela.nome]
  return [{ rotulo: registro.grupo ?? "Contratos" }, { rotulo: registro.trilha }]
}
